import React from 'react';

import { LiveTvChannel } from '@smart-tv/typings/content';

import {
  DEFAULT_FAVORITES_LIST,
  FavoritesList
} from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';

import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';

import { getEpgDataNowNextForChannels } from '@smart-tv/utils/kmm/getEpgDataNowNextForChannels';

export enum FavoritesScreen {
  Home = 'HOME',
  Edit = 'EDIT',
  Title = 'TITLE',
  Save = 'SAVE',
  Delete = 'DELETE'
}

export enum FavoritesListType {
  Existing = 'EXISTING',
  New = 'NEW',
  Default = 'DEFAULT'
}

export enum SaveType {
  Escape = 'ESCAPE',
  Save = 'SAVE'
}

export interface FavoritesContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface FavoritesEditContextValues {
  listID: number;
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  openFeedbackDialog: boolean;
  selectedScreen: FavoritesScreen;
  handleSelectScreen: (screen: FavoritesScreen) => void;
  handleCloseFeedbackDialog: () => void;
  channels?: LiveTvChannel[];
  usedFavoritesLists?: FavoritesList[];
  emptyFavoritesLists?: FavoritesList[];
  initialFavoritesListType?: FavoritesListType;
  editedFavoritesList?: FavoritesList;
  setEditedFavoritesList: React.Dispatch<React.SetStateAction<FavoritesList | undefined>>;
  updateSids: (computeNewSids: (prevSids: number[]) => number[]) => void;
  handleEditedFavoritesListNameChange: (name: string) => void;
  handleChannelToggle: (sid: number) => void;
  lastToggledChannel: { sid: number; wasAdded: boolean } | null;
  handleSetInitialFavoriteList: (id: number) => void;
  saveType?: SaveType;
  setSaveType: React.Dispatch<React.SetStateAction<SaveType | undefined>>;
  hasChanges: boolean;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  handleBackNavigation: () => void;
}

export const FavoritesEditContext = React.createContext<FavoritesEditContextValues>(
  {} as FavoritesEditContextValues
);

export const FavoritesEditConsumer = FavoritesEditContext.Consumer;

export const FavoritesEditProvider = (props: FavoritesContextProps): JSX.Element => {
  const { children } = props;

  const { favoritLists } = useFavoritesLists();

  const [listID, setListID] = React.useState<number>(-1);
  const [open, setOpen] = React.useState(false);
  const [selectedScreen, setSelectedScreen] = React.useState<FavoritesScreen>(FavoritesScreen.Home);
  const [openFeedbackDialog, setOpenFeedbackDialog] = React.useState(false);

  const [channels, setChannels] = React.useState<LiveTvChannel[]>();

  const [usedFavoritesLists, setUsedFavoritesLists] = React.useState<FavoritesList[]>([]);
  const [emptyFavoritesLists, setEmptyFavoritesLists] = React.useState<FavoritesList[]>([]);

  const [editedFavoritesList, setEditedFavoritesList] = React.useState<FavoritesList>();
  const [initialFavoritesListType, setInitialFavoritesListType] =
    React.useState<FavoritesListType>();
  const [saveType, setSaveType] = React.useState<SaveType>();
  const [hasChanges, setHasChanges] = React.useState(false);
  const [lastToggledChannel, setLastToggledChannel] = React.useState<{
    sid: number;
    wasAdded: boolean;
  } | null>(null);

  // Authoritative, synchronously-updated mirror of editedFavoritesList.sids. Every sids mutation
  // (toggle, reorder, wholesale list replace) MUST go through updateSids/setEditedList below so
  // this never diverges from a queued-but-not-yet-rendered state update when editing fast.
  const sidsRef = React.useRef<number[]>(editedFavoritesList?.sids ?? []);

  const setEditedList = React.useCallback((list: FavoritesList | undefined) => {
    sidsRef.current = list?.sids ?? [];
    setEditedFavoritesList(list);
  }, []);

  const updateSids = React.useCallback((computeNewSids: (prevSids: number[]) => number[]) => {
    const newSids = computeNewSids(sidsRef.current);
    sidsRef.current = newSids;
    setEditedFavoritesList((prevState) => prevState && { ...prevState, sids: newSids });
  }, []);

  const handleSelectScreen = React.useCallback((screen: FavoritesScreen) => {
    setSelectedScreen(screen);
  }, []);

  const handleBackNavigation = React.useCallback(() => {
    const isExistingList = usedFavoritesLists?.some((list) => list.id === editedFavoritesList?.id);
    const listIsEmpty = editedFavoritesList?.sids.length === 0;
    if (hasChanges && (isExistingList || !listIsEmpty)) {
      setSaveType(SaveType.Escape);
      handleSelectScreen(FavoritesScreen.Save);
    } else {
      handleSelectScreen(FavoritesScreen.Home);
      setHasChanges(false);
    }
  }, [
    hasChanges,
    setSaveType,
    handleSelectScreen,
    setHasChanges,
    usedFavoritesLists,
    editedFavoritesList
  ]);

  const handleCloseFeedbackDialog = React.useCallback(() => {
    setOpenFeedbackDialog(false);
  }, []);

  const setInitialName = React.useCallback(() => {
    const name = 'Liste ';
    let number = 1;

    while (usedFavoritesLists.find((list) => list.name === name + number)) {
      number++;
    }

    return name + number;
  }, [usedFavoritesLists]);

  const handleSetInitialFavoriteList = React.useCallback(
    (id: number) => {
      setListID(id);
      if (id >= 0) {
        const favoriteList = usedFavoritesLists.find((list) => list.id === id);

        setEditedList(favoriteList);
        setInitialFavoritesListType(FavoritesListType.Existing);
      } else if (id === DEFAULT_FAVORITES_LIST.id) {
        setEditedList(undefined);
        setInitialFavoritesListType(FavoritesListType.Default);
      } else {
        const newFavoritesList = {
          id: emptyFavoritesLists[0].id,
          name: setInitialName(),
          sids: []
        };
        setEditedList(newFavoritesList);
        setInitialFavoritesListType(FavoritesListType.New);
      }
    },
    [emptyFavoritesLists, setEditedList, setInitialName, usedFavoritesLists]
  );

  const handleOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleEditedFavoritesListNameChange = React.useCallback((name: string) => {
    setEditedFavoritesList((prevState) => prevState && { ...prevState, name });
    setHasChanges(true);
  }, []);

  const handleChannelToggle = React.useCallback(
    (selectedSid: number) => {
      const alreadySelected = sidsRef.current.includes(selectedSid);
      const wasAdded = !alreadySelected;

      updateSids((prevSids) =>
        alreadySelected ? prevSids.filter((sid) => sid !== selectedSid) : [...prevSids, selectedSid]
      );
      setLastToggledChannel({ sid: selectedSid, wasAdded });
      setHasChanges(true);
    },
    [updateSids]
  );

  React.useEffect(() => {
    (async function () {
      const data = await getEpgDataNowNextForChannels({
        favoriteList: DEFAULT_FAVORITES_LIST.id,
        nextCount: 0
      });
      setChannels(data.channels);
    })();
  }, []);

  React.useEffect(() => {
    if (favoritLists && open) {
      const newUsedFavoritesLists = [
        DEFAULT_FAVORITES_LIST,
        ...favoritLists.filter(({ sids }) => sids.length > 0)
      ];
      const emptyFavoritesLists = favoritLists.filter(({ sids }) => sids.length === 0);

      setUsedFavoritesLists(newUsedFavoritesLists);
      setEmptyFavoritesLists(emptyFavoritesLists);

      if (editedFavoritesList) {
        const updatedList = newUsedFavoritesLists.find(
          (list) => list.id === editedFavoritesList.id
        );
        if (updatedList) {
          setEditedList(updatedList);
        }
      }
    }
  }, [favoritLists, open]);

  return (
    <FavoritesEditContext.Provider
      value={{
        listID,
        open,
        handleOpen,
        handleClose,
        selectedScreen,
        handleSelectScreen,
        openFeedbackDialog,
        handleCloseFeedbackDialog,
        channels,
        usedFavoritesLists,
        emptyFavoritesLists,
        initialFavoritesListType,
        editedFavoritesList,
        setEditedFavoritesList,
        updateSids,
        handleEditedFavoritesListNameChange,
        handleChannelToggle,
        lastToggledChannel,
        handleSetInitialFavoriteList,
        saveType,
        setSaveType,
        hasChanges,
        setHasChanges,
        handleBackNavigation
      }}
    >
      {children}
    </FavoritesEditContext.Provider>
  );
};

export default FavoritesEditContext;
